import { usePageTitle } from "@/lib/usePageTitle";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone,
  Printer,
  Mail,
  MapPin,
  Clock,
  Send,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import FadeIn from "@/components/site/FadeIn";
import { PageHero } from "@/components/site/ui";
import { COMPANY } from "@/data/content";
import { apiClient } from "@/lib/api";
import { useLocale } from "@/i18n/LocaleContext";
import WhatsAppIcon from "@/components/site/WhatsAppIcon";
import { whatsappHref } from "@/lib/whatsapp";

const MAPS_EMBED =
  "https://www.google.com/maps?q=Konrad-Duden-Weg+3,+60437+Frankfurt+am+Main&output=embed";
const MAPS_LINK =
  "https://www.google.com/maps/search/?api=1&query=Konrad-Duden-Weg+3,+60437+Frankfurt+am+Main";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Kontakt() {
  usePageTitle("contact");
  const { t, path } = useLocale();
  const c = t.contact;
  const waHref = whatsappHref(t.common.whatsappText);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = c.errName;
    if (!emailRe.test(form.email)) e.email = c.errEmail;
    if (form.message.trim().length < 5) e.message = c.errMessage;
    if (!form.consent) e.consent = c.errConsent;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error(c.toastCheck);
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: c.subject,
        message: form.message.trim(),
        consent: form.consent,
      });
      setSent(true);
      toast.success(c.toastSuccess);
      setForm({ name: "", email: "", phone: "", message: "", consent: false });
    } catch (err) {
      toast.error(c.toastError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow={c.eyebrow}
        title={c.title}
        subtitle={c.subtitle}
      />

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Formular */}
            <FadeIn>
              <h2 className="text-3xl font-600 text-brand-dark">
                {c.formTitle}
              </h2>
              <p className="mt-3 text-lg text-muted-foreground">{c.formLead}</p>

              {sent ? (
                <div
                  data-testid="contact-success"
                  className="mt-8 rounded-2xl bg-brand-light border border-brand/20 p-8 text-center"
                >
                  <CheckCircle2 className="w-14 h-14 text-brand mx-auto" />
                  <h3 className="mt-4 text-2xl font-600 text-brand-dark">
                    {c.successTitle}
                  </h3>
                  <p className="mt-2 text-lg text-ink">{c.successText}</p>
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    data-testid="contact-new-message"
                    className="mt-6 inline-flex items-center gap-2 rounded-xl border-2 border-brand px-6 py-3 font-600 text-brand hover:bg-brand-light transition-colors"
                  >
                    {c.sendAnother}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate className="mt-8 space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="text-base font-600 text-ink">
                      {c.name} <span className="text-coral-hover">*</span>
                    </Label>
                    <Input
                      id="name"
                      data-testid="contact-name"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      placeholder={c.namePlaceholder}
                      className="mt-2 h-12 text-lg rounded-xl bg-cream border-border focus-visible:ring-4 focus-visible:ring-brand/30"
                      aria-invalid={!!errors.name}
                    />
                    {errors.name && (
                      <p className="mt-1.5 text-base text-destructive" data-testid="error-name">{errors.name}</p>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-base font-600 text-ink">
                        {c.email} <span className="text-coral-hover">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-email"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder={c.emailPlaceholder}
                        className="mt-2 h-12 text-lg rounded-xl bg-cream border-border focus-visible:ring-4 focus-visible:ring-brand/30"
                        aria-invalid={!!errors.email}
                      />
                      {errors.email && (
                        <p className="mt-1.5 text-base text-destructive" data-testid="error-email">{errors.email}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-base font-600 text-ink">
                        {c.phone}
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="contact-phone"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        placeholder={c.phonePlaceholder}
                        className="mt-2 h-12 text-lg rounded-xl bg-cream border-border focus-visible:ring-4 focus-visible:ring-brand/30"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-base font-600 text-ink">
                      {c.message} <span className="text-coral-hover">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message"
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      placeholder={c.messagePlaceholder}
                      rows={5}
                      className="mt-2 text-lg rounded-xl bg-cream border-border focus-visible:ring-4 focus-visible:ring-brand/30"
                      aria-invalid={!!errors.message}
                    />
                    {errors.message && (
                      <p className="mt-1.5 text-base text-destructive" data-testid="error-message">{errors.message}</p>
                    )}
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consent"
                      data-testid="contact-consent"
                      checked={form.consent}
                      onCheckedChange={(v) => set("consent", v === true)}
                      className="mt-1 h-6 w-6 rounded-md border-brand data-[state=checked]:bg-brand"
                    />
                    <Label htmlFor="consent" className="text-base text-ink leading-relaxed cursor-pointer font-400">
                      {c.consentBefore}{" "}
                      <Link to={path("privacy")} className="text-brand font-600 underline underline-offset-2">
                        {c.consentLink}
                      </Link>{" "}
                      {c.consentAfter} <span className="text-coral-hover">*</span>
                    </Label>
                  </div>
                  {errors.consent && (
                    <p className="-mt-3 text-base text-destructive" data-testid="error-consent">{errors.consent}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand px-8 py-4 text-lg font-600 text-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-hover hover:shadow-lg disabled:opacity-60 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/40"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" /> {c.sending}
                      </>
                    ) : (
                      <>
                        {c.submit} <Send className="w-5 h-5 rtl:-scale-x-100" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </FadeIn>

            {/* Kontaktdaten */}
            <FadeIn delay={0.12}>
              <div className="rounded-2xl bg-brand-dark text-white p-8 md:p-10">
                <h2 className="text-2xl font-600">{c.reachTitle}</h2>
                <ul className="mt-6 space-y-5">
                  <ContactRow icon={MapPin} label={c.address}>
                    <span className="ltr">
                      {COMPANY.address.street}
                      <br />
                      {COMPANY.address.zip} {COMPANY.address.city}
                    </span>
                  </ContactRow>
                  <ContactRow icon={Phone} label={c.phoneLabel}>
                    <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-gold">{COMPANY.phone}</a>
                  </ContactRow>
                  <ContactRow icon={Printer} label={c.fax}>
                    <span className="ltr">{COMPANY.fax}</span>
                  </ContactRow>
                  <ContactRow icon={Clock} label={c.emergency}>
                    <a href={`tel:${COMPANY.emergencyRaw}`} className="hover:text-gold">{COMPANY.emergency}</a>
                    <span className="text-white/60"> · {c.alternatively} <span className="ltr">{COMPANY.emergencyAlt}</span></span>
                  </ContactRow>
                  <ContactRow icon={Mail} label={c.emailLabel}>
                    <a href={`mailto:${COMPANY.email}`} className="hover:text-gold break-all">{COMPANY.email}</a>
                  </ContactRow>
                  {waHref && (
                    <ContactRow icon={WhatsAppIcon} label={t.common.whatsapp}>
                      <a
                        href={waHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-whatsapp"
                        className="hover:text-gold"
                      >
                        <span className="ltr">{COMPANY.whatsapp}</span>
                      </a>
                      <span className="mt-1 block text-sm text-white/60 leading-snug">
                        {t.common.whatsappNote}
                      </span>
                    </ContactRow>
                  )}
                </ul>

                <div className="mt-8 rounded-xl bg-white/5 border border-white/10 p-5">
                  <h3 className="font-serif text-lg font-600">{c.hoursTitle}</h3>
                  <p className="mt-2 text-white/80">{c.hours}</p>
                  <p className="text-white/80">
                    {c.careAndEmergency}{" "}
                    <span className="font-600 text-white">{c.aroundTheClock}</span>
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Google Maps, DSGVO Klick-zum-Laden */}
          <FadeIn>
            <div className="mt-12">
              <h2 className="text-2xl font-600 text-brand-dark">
                {c.mapTitle}
              </h2>
              <div className="mt-5 rounded-2xl overflow-hidden border border-border bg-cream">
                {mapLoaded ? (
                  <iframe
                    title={c.mapIframeTitle}
                    src={MAPS_EMBED}
                    className="w-full h-[420px] border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid="maps-iframe"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-center h-[420px] p-8">
                    <MapPin className="w-12 h-12 text-brand" />
                    <h3 className="mt-4 text-xl font-600 text-brand-dark">
                      {c.mapLoad}
                    </h3>
                    <p className="mt-2 max-w-md text-base text-muted-foreground">{c.mapNotice}</p>
                    <button
                      type="button"
                      onClick={() => setMapLoaded(true)}
                      data-testid="maps-consent-button"
                      className="mt-5 inline-flex items-center gap-2 rounded-xl bg-brand px-6 py-3 font-600 text-white hover:bg-brand-hover transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/40"
                    >
                      {c.mapButton}
                    </button>
                    <a
                      href={MAPS_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 text-base text-brand underline underline-offset-2 hover:text-brand-dark"
                    >
                      {c.mapAlt}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

const ContactRow = ({ icon: Icon, label, children }) => (
  <li className="flex items-start gap-3.5">
    <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 text-gold shrink-0">
      <Icon className="w-5 h-5" />
    </span>
    <div>
      <p className="text-sm text-white/60">{label}</p>
      <p className="text-lg text-white leading-snug">{children}</p>
    </div>
  </li>
);
